import React from 'react';
import type { InventoryItem } from '../../../types/inventory';
import type {
  InventoryTypeFilter,
  RoastProfileContext,
  FinishedGoodsTasteProfileContext,
} from '../../../hooks/inventory-hooks/useInventoryPageState';
import { InventoryDesktopTable } from './InventoryDesktopTable';
import { InventoryMobileList } from './InventoryMobileList';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventoryTableProps {
  typeFilter: InventoryTypeFilter;
  items: InventoryItem[];
  sortConfig: { key: keyof InventoryItem; direction: 'asc' | 'desc' } | null;
  onSort: (key: keyof InventoryItem) => void;
  expandedRowIds: Set<string>;
  onToggleRow: (id: string) => void;
  currencySymbol: string;
  onRowClick: (item: InventoryItem) => void;
  onEditItem: (item: InventoryItem) => void;
  onOpenHistory: (item: InventoryItem) => void;
  onOpenRoastProfile: (context: RoastProfileContext) => void;
  onOpenTasteProfile: (context: FinishedGoodsTasteProfileContext) => void;
  onRestock: (item: InventoryItem) => void;
  onDeleteItem: (item: InventoryItem) => void;
  canWrite: boolean;
  canDelete: boolean;
  formatDate: (dateString: string | Date) => string;
  onProfileUpdated: () => void;
}

export const InventoryTable: React.FC<InventoryTableProps> = ({
  typeFilter,
  items,
  sortConfig,
  onSort,
  expandedRowIds,
  onToggleRow,
  currencySymbol,
  onRowClick,
  onEditItem,
  onOpenHistory,
  onOpenRoastProfile,
  onOpenTasteProfile,
  onRestock,
  onDeleteItem,
  canWrite,
  canDelete,
  formatDate,
  onProfileUpdated,
}) => {
  const { t } = useLanguage();

  if (items.length === 0) {
    return (
      <div className="px-4 py-5 sm:p-6 text-center text-gray-500 italic">
        {t('inventory.finishedGoods.emptyInventory')}
      </div>
    );
  }

  return (
    <>
      <InventoryDesktopTable
        typeFilter={typeFilter}
        items={items}
        sortConfig={sortConfig}
        onSort={onSort}
        expandedRowIds={expandedRowIds}
        onToggleRow={onToggleRow}
        currencySymbol={currencySymbol}
        onRowClick={onRowClick}
        onEditItem={onEditItem}
        onOpenHistory={onOpenHistory}
        onOpenRoastProfile={onOpenRoastProfile}
        onOpenTasteProfile={onOpenTasteProfile}
        onRestock={onRestock}
        onDeleteItem={onDeleteItem}
        canWrite={canWrite}
        canDelete={canDelete}
        onProfileUpdated={onProfileUpdated}
      />
      <InventoryMobileList
        items={items}
        expandedRowIds={expandedRowIds}
        onToggleRow={onToggleRow}
        currencySymbol={currencySymbol}
        onRowClick={onRowClick}
        onEditItem={onEditItem}
        onOpenHistory={onOpenHistory}
        onOpenRoastProfile={onOpenRoastProfile}
        onOpenTasteProfile={onOpenTasteProfile}
        onRestock={onRestock}
        canWrite={canWrite}
        formatDate={formatDate}
      />
    </>
  );
};
